import {
  FILTER_OPTION_NO_VALUE,
  parseDateRangeFilterValueStrings,
} from '@/components/data-table/filters-v2';
import { FilterValues } from '@/hooks/use-filter-values';
import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';
import { ProjectStatusRequest } from '@/modules/projects/types/dto/project-status.dto';
import { ProjectStatus } from '@/modules/projects/types/project';
import { MembershipType } from '@/modules/team/types/membership-type';
import { readOptionCityId } from '@/modules/team/utils/city-option';

import {
  PLANNER_ASSIGNMENT_PROBABILITY_FILTER_KEY,
  PLANNER_AVAILABILITY_FILTER_KEY,
  PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY,
  PLANNER_DATE_RANGE_FILTER_KEY,
  PLANNER_IS_MANAGER_FILTER_KEY,
  PLANNER_MEMBER_MANAGER_FILTER_KEY,
  PLANNER_PROJECT_MANAGER_FILTER_KEY,
  PLANNER_PROJECT_PROBABILITY_FILTER_KEY,
  PLANNER_PROJECT_STATUS_FILTER_KEY,
} from '../constants';
import {
  GetAssignmentsForPlannerFiltersRequest,
  RangeScopedPlannerFiltersRequest,
} from '../types/dto';
import { mapAssignmentStatusToDTO } from './assignment-status.mapper';
import { readRangeBounds } from './helpers/read-range-bounds';

type FilterValue = string | string[] | undefined;

/** Normalizes a filter value to an array (empty when unset). */
const toFilterArray = (value: FilterValue): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/** Reads the single value of a filter stored as a one-element array or bare string. */
const toSingleFilterValue = (value: FilterValue): string | undefined =>
  Array.isArray(value) ? value[0] : value;

/**
 * Maps UI filter values to API request format for planner assignment queries.
 * Handles conversion of filter types including location filters with country-city structure,
 * range mode filters for project/assignment probability, and status mapping.
 */
export const mapAssignmentFilters = ({
  startDate,
  endDate,
  filters,
}: {
  startDate: string;
  endDate: string;
  filters: FilterValues['values'];
}): GetAssignmentsForPlannerFiltersRequest => {
  const apiFilters: GetAssignmentsForPlannerFiltersRequest = {
    startDate,
    endDate,
  };

  if (filters.searchString) {
    apiFilters.searchQuery = filters.searchString as string;
  }

  if (filters.user) {
    apiFilters.userIds = toFilterArray(filters.user).map(Number);
  }

  // Maps a multiselect value to a nullable id list: the "no value" UI sentinel
  // ("No department" / "No role" / … options) becomes a null entry — the backend
  // matches NULL columns for it.
  const readNullableIds = (value: string | string[]): Array<number | null> =>
    toFilterArray(value).map(v => (v === FILTER_OPTION_NO_VALUE ? null : Number(v)));

  if (filters.department) {
    const ids = readNullableIds(filters.department);
    if (ids.length > 0) apiFilters.departmentIds = ids;
  }

  if (filters.role) {
    const ids = readNullableIds(filters.role);
    if (ids.length > 0) apiFilters.roleIds = ids;
  }

  if (filters.seniority) {
    const ids = readNullableIds(filters.seniority);
    if (ids.length > 0) apiFilters.seniorityIds = ids;
  }

  if (filters[PLANNER_MEMBER_MANAGER_FILTER_KEY]) {
    const ids = readNullableIds(filters[PLANNER_MEMBER_MANAGER_FILTER_KEY]);
    if (ids.length > 0) apiFilters.memberManagerIds = ids;
  }

  // Handle location filters with country-city structure
  if (filters.country || filters.city) {
    const allCountries = toFilterArray(filters.country);
    const allCities = toFilterArray(filters.city);

    const countries = allCountries.filter(country => country !== FILTER_OPTION_NO_VALUE);
    const cities = allCities.filter(cityId => cityId !== FILTER_OPTION_NO_VALUE);
    if (allCountries.includes(FILTER_OPTION_NO_VALUE)) apiFilters.withoutCountry = true;
    if (allCities.includes(FILTER_OPTION_NO_VALUE)) apiFilters.withoutCity = true;

    // If both country and city are selected, create location filter with cities
    if (countries.length > 0 && cities.length > 0) {
      apiFilters.locationFilters = countries.map(country => ({
        country: String(country),
        cities: cities
          .filter(cityId => readOptionCityId(cityId).country === country)
          .map(cityId => readOptionCityId(cityId).city),
      }));
    }
    // If only country is selected, filter by entire country
    else if (countries.length > 0) {
      apiFilters.locationFilters = countries.map(country => ({
        country: String(country),
      }));
    }
  }

  if (filters.projectName) {
    apiFilters.projectIds = toFilterArray(filters.projectName).map(Number);
  }

  if (filters.client) {
    const ids = readNullableIds(filters.client);
    if (ids.length > 0) apiFilters.clientIds = ids;
  }

  if (filters[PLANNER_PROJECT_MANAGER_FILTER_KEY]) {
    const ids = readNullableIds(filters[PLANNER_PROJECT_MANAGER_FILTER_KEY]);
    if (ids.length > 0) apiFilters.projectManagerIds = ids;
  }

  const capacityBounds = readRangeBounds({ value: filters.capacity, multiplier: 60 });
  if (capacityBounds) {
    apiFilters.capacityMinutesMinInclusive = capacityBounds.minInclusive;
    apiFilters.capacityMinutesMaxInclusive = capacityBounds.maxInclusive;
    apiFilters.capacityMinutesMinExclusive = capacityBounds.minExclusive;
    apiFilters.capacityMinutesMaxExclusive = capacityBounds.maxExclusive;
  }

  const billableCapacityBounds = readRangeBounds({
    value: filters.billableCapacity,
    multiplier: 60,
  });
  if (billableCapacityBounds) {
    apiFilters.billableCapacityMinutesMinInclusive = billableCapacityBounds.minInclusive;
    apiFilters.billableCapacityMinutesMaxInclusive = billableCapacityBounds.maxInclusive;
    apiFilters.billableCapacityMinutesMinExclusive = billableCapacityBounds.minExclusive;
    apiFilters.billableCapacityMinutesMaxExclusive = billableCapacityBounds.maxExclusive;
  }

  // Probability requires projectStatus to include TENTATIVE — the store cascade
  // keeps them in sync, this guard covers stale/hand-edited persisted values.
  const hasTentativeProjectStatus = toFilterArray(
    filters[PLANNER_PROJECT_STATUS_FILTER_KEY]
  ).includes(ProjectStatus.Tentative);

  // Probability is a percent (0-100) with no unit scaling, but otherwise follows the
  // same including-toggle semantics as capacity/availability (inclusive >= / <= vs
  // strict > / <, Equal always inclusive).
  if (hasTentativeProjectStatus) {
    const projectProbabilityBounds = readRangeBounds({
      value: filters[PLANNER_PROJECT_PROBABILITY_FILTER_KEY],
      multiplier: 1,
    });
    if (projectProbabilityBounds) {
      apiFilters.projectProbabilityMinInclusive = projectProbabilityBounds.minInclusive;
      apiFilters.projectProbabilityMaxInclusive = projectProbabilityBounds.maxInclusive;
      apiFilters.projectProbabilityMinExclusive = projectProbabilityBounds.minExclusive;
      apiFilters.projectProbabilityMaxExclusive = projectProbabilityBounds.maxExclusive;
    }
  }

  const assignmentProbabilityBounds = readRangeBounds({
    value: filters[PLANNER_ASSIGNMENT_PROBABILITY_FILTER_KEY],
    multiplier: 1,
  });
  if (assignmentProbabilityBounds) {
    apiFilters.assignmentProbabilityMinInclusive = assignmentProbabilityBounds.minInclusive;
    apiFilters.assignmentProbabilityMaxInclusive = assignmentProbabilityBounds.maxInclusive;
    apiFilters.assignmentProbabilityMinExclusive = assignmentProbabilityBounds.minExclusive;
    apiFilters.assignmentProbabilityMaxExclusive = assignmentProbabilityBounds.maxExclusive;
  }

  // Manager multiselect: exactly one option selected filters by it; both or
  // neither selected means "all members" and the field is omitted.
  const isManagerValues = toFilterArray(filters[PLANNER_IS_MANAGER_FILTER_KEY]);
  if (isManagerValues.length === 1) {
    apiFilters.isManager = isManagerValues[0] === 'true';
  }

  if (filters.status) {
    apiFilters.statuses = toFilterArray(filters.status).map(mapAssignmentStatusToDTO);
  }

  if (filters.projectStatus) {
    // Option values are the wire enum values (ProjectStatusDTO)
    apiFilters.projectStatuses = toFilterArray(filters.projectStatus) as ProjectStatusRequest[];
  }

  if (filters.memberStatus) {
    apiFilters.memberStatuses = toFilterArray(filters.memberStatus) as TeamMemberStatusDTO[];
  }

  if (filters.membershipType) {
    apiFilters.membershipTypes = toFilterArray(filters.membershipType) as MembershipType[];
  }

  const customAttrFilters = mapCustomAttributeFilters(filters);
  if (customAttrFilters) {
    apiFilters.customAttributeFilters = customAttrFilters;
  }

  // The availability bounds live INSIDE rangeScopedFilters, whose filtersDateRange
  // is required — without a date range they are never emitted at all.
  const dateRangeRaw = toSingleFilterValue(filters[PLANNER_DATE_RANGE_FILTER_KEY]);
  const filtersDateRange = dateRangeRaw ? parseDateRangeFilterValueStrings(dateRangeRaw) : null;
  if (filtersDateRange) {
    const rangeScopedFilters: RangeScopedPlannerFiltersRequest = { filtersDateRange };

    const availabilityBounds = readRangeBounds({
      value: filters[PLANNER_AVAILABILITY_FILTER_KEY],
      multiplier: 60,
    });
    if (availabilityBounds) {
      rangeScopedFilters.freeMinutesPerDayMinInclusive = availabilityBounds.minInclusive;
      rangeScopedFilters.freeMinutesPerDayMaxInclusive = availabilityBounds.maxInclusive;
      rangeScopedFilters.freeMinutesPerDayMinExclusive = availabilityBounds.minExclusive;
      rangeScopedFilters.freeMinutesPerDayMaxExclusive = availabilityBounds.maxExclusive;
    }

    const billableAvailabilityBounds = readRangeBounds({
      value: filters[PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY],
      multiplier: 60,
    });
    if (billableAvailabilityBounds) {
      rangeScopedFilters.billableFreeMinutesPerDayMinInclusive =
        billableAvailabilityBounds.minInclusive;
      rangeScopedFilters.billableFreeMinutesPerDayMaxInclusive =
        billableAvailabilityBounds.maxInclusive;
      rangeScopedFilters.billableFreeMinutesPerDayMinExclusive =
        billableAvailabilityBounds.minExclusive;
      rangeScopedFilters.billableFreeMinutesPerDayMaxExclusive =
        billableAvailabilityBounds.maxExclusive;
    }

    apiFilters.rangeScopedFilters = rangeScopedFilters;
  }

  return apiFilters;
};
